import React, { useTransition } from 'react';
import { View, Text } from 'react-native';
import { Icon } from '@ant-design/react-native';
import ParameterItem from './ParameterItem';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import type { ICurrentPathway } from 'app/types/pathway';
import { colorPalette } from '@shared/config/colorsConfig';

interface MonitoringSectionProps {
  pathway?: ICurrentPathway;
}

export default function MonitoringSection({ pathway }: MonitoringSectionProps) {
  const { t } = dynamicTranslations();

  const getTranslatedFrequency = (frequency: string, daySection?: string) => {
    let displayFrequency = frequency;

    if (daySection) {
      displayFrequency = `${t("screens.profile.pathDetails.frequency." + frequency)} - ${t("screens.profile.pathDetails.day-sections." + daySection, daySection)}`;
    }

    return displayFrequency;
  };

  const renderVitalParameters = () => {
    if (!pathway?.vital_parameters?.length) {
      return (
        <Text className="text-sm text-gray-500">
          {t('screens.profile.pathDetails.no-vital-parameters', 'N/A')}
        </Text>
      );
    }
    return pathway.vital_parameters.map((vp, index) => {
      const name = vp.vital_parameter?.name || '';
      const translatedFrequency = getTranslatedFrequency(
        vp.frequency || '',
        vp.first_selected_day_section,
      );

      return (
        <ParameterItem
          key={`vp-${index}-${name}`}
          titleKey={name}
          frequencyKey={translatedFrequency}
        />
      );
    });
  };

  const renderQuestionnaires = () => {
    if (!pathway?.questionnaires?.length) {
      return (
        <Text className="text-sm text-gray-500">
          {t('screens.profile.pathDetails.no-questionnaires', 'N/A')}
        </Text>
      );
    }

    return pathway.questionnaires.map((q, index) => {
      const name = q.questionnaire?.label || '';
      const translatedFrequency = getTranslatedFrequency(
        q.frequency || '',
        q.first_selected_day_section,
      );

      return (
        <ParameterItem
          key={`q-${index}-${name}`}
          titleKey={name}
          frequencyKey={translatedFrequency}
        />
      );
    });
  };

  const renderExams = () => {
    if (!pathway?.exams?.length) {
      return (
        <Text className="text-sm text-gray-500">
          {t('screens.profile.pathDetails.no-exams', 'N/A')}
        </Text>
      );
    }

    return pathway.exams.map((e, index) => {
      const name = e.exam?.name || '';
      const translatedFrequency = getTranslatedFrequency(e.frequency || '');

      return (
        <ParameterItem
          key={`exam-${index}-${name}`}
          titleKey={name}
          frequencyKey={translatedFrequency}
        />
      );
    });
  };

  return (
    <View className="bg-white rounded-xl mx-4 mb-2 shadow-sm border border-gray-200">
      <View className="p-4 border-b border-gray-100">
        <Text className="text-lg font-bold text-black">
          {t('screens.profile.pathDetails.what-includes')}
        </Text>
      </View>

      <View className="p-4">
        <View className="flex-row items-center mb-4">
          <Icon name="line-chart" size={24} color={colorPalette.primary[600]} />
          <Text className="text-base font-bold ml-3 text-black">
            {t('screens.profile.pathDetails.monitor-vitals')}
          </Text>
        </View>

        <View className="mb-6">
          {renderVitalParameters()}
        </View>

        <View className="h-px bg-gray-200 mb-6" />
        <View className="flex-row items-center mb-4">
          <Icon name="question-circle" size={24} color={colorPalette.primary[600]} />
          <Text className="text-base font-bold ml-3 text-black">
            {t('screens.profile.pathDetails.health-questionnaires')}
          </Text>
        </View>

        <View className="mb-6">
          {renderQuestionnaires()}
        </View>

        <View className="h-px bg-gray-200 mb-6" />
        <View className="flex-row items-center mb-4">
          <Icon name="medicine-box" size={24} color={colorPalette.primary[600]} />
          <Text className="text-base font-bold ml-3 text-black">
            {t('screens.profile.pathDetails.recommended-exams')}
          </Text>
        </View>

        <View>
          {renderExams()}
        </View>
      </View>
    </View>
  );
}
